import { Injectable, signal } from '@angular/core';
import { Toast, ToastVariant } from './toast.models';

/** Most toasts visible at once — older ones are dropped when a new one pushes past this. */
const MAX_VISIBLE = 4;
/** Time the leave transition is given to play before the toast is removed from the DOM. */
const LEAVE_MS = 200;
/** Default auto-dismiss delay per variant. Errors linger longer; nothing is sticky. */
const DEFAULT_DURATION_MS: Record<ToastVariant, number> = {
  success: 3000,
  info: 3000,
  warning: 4000,
  error: 6000,
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private nextId = 0;

  /** Queue a toast. Returns its id so callers can dismiss it early if they need to. */
  show(text: string, variant: ToastVariant = 'info', durationMs?: number): number {
    const id = this.nextId++;
    this._toasts.update((list) => {
      const next = [...list, { id, variant, text, leaving: false }];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    setTimeout(() => this.dismiss(id), durationMs ?? DEFAULT_DURATION_MS[variant]);
    return id;
  }

  success(text: string, durationMs?: number): number {
    return this.show(text, 'success', durationMs);
  }

  error(text: string, durationMs?: number): number {
    return this.show(text, 'error', durationMs);
  }

  info(text: string, durationMs?: number): number {
    return this.show(text, 'info', durationMs);
  }

  warning(text: string, durationMs?: number): number {
    return this.show(text, 'warning', durationMs);
  }

  /** Start the leave transition, then remove the toast once it has played. */
  dismiss(id: number): void {
    const target = this._toasts().find((t) => t.id === id);
    if (!target || target.leaving) return;

    this._toasts.update((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      this._toasts.update((list) => list.filter((t) => t.id !== id));
    }, LEAVE_MS);
  }
}
